import { Box, useBreakpointValue, useTheme } from '@cmpsr/components';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';

export type RiskSpecificCancerChartProps = {
  risks: number[];
  riskChanges: number[];
};

export default function RiskSpecificCancerChart({ risks, riskChanges }: RiskSpecificCancerChartProps) {
  const { colors } = useTheme();

  const chartHeightInput = useBreakpointValue({ base: '156px', md: '216px', lg: '128px' }, { ssr: false });
  const chartHeight = chartHeightInput ?? '156px';

  const chartData = risks.map((value, index) => ({
    x: index + 1,
    y: value,
  }));

  // Y-Axis Domain calculation
  let yDomain: [number, number];
  const yPadding = 5;
  if (risks.length === 0) {
    yDomain = [0, 10 + yPadding * 2];
  } else {
    const yMin = Math.min(...risks);
    const yMax = Math.max(...risks);
    if (yMin === yMax) {
      yDomain = [yMin - yPadding, yMax + yPadding];
    } else {
      yDomain = [yMin - yPadding, yMax + yPadding];
    }
    if (yDomain[0] >= yDomain[1]) {
      yDomain[1] = yDomain[0] + yPadding * 2;
    }
  }

  // X-Axis Domain (1-based index)
  let xDomain: [number | 'auto' | 'dataMin' | 'dataMax', number | 'auto' | 'dataMin' | 'dataMax'];
  if (chartData.length === 0) {
    xDomain = [0.5, 1.5];
  } else if (chartData.length === 1) {
    xDomain = [chartData[0].x - 0.5, chartData[0].x + 0.5];
  } else {
    xDomain = ['dataMin', 'dataMax'];
  }

  return (
    <Box height={chartHeight}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
          <XAxis dataKey="x" type="number" hide domain={xDomain} axisLine={false} tick={false} />
          <YAxis type="number" hide domain={yDomain} allowDataOverflow axisLine={false} tick={false} />
          <Tooltip active={false} />
          <Line
            type="linear"
            dataKey="y"
            name="Your specific cancer trend"
            stroke={colors['primary-default']}
            strokeWidth={1.5}
            dot={{ r: 6, strokeWidth: 0, fill: colors['primary-default'] }}
            activeDot={false}
            isAnimationActive={false}
          />
          {riskChanges
            .map((value, index) => {
              if (index === 0) return null; // Skip first annotation

              if (risks[index] === undefined || risks[index] === null) return null;

              const markerColor = value >= 0 ? colors['alert-error-default'] : colors['alert-success-default'];

              return (
                <ReferenceDot
                  key={`anno-${index}`}
                  x={index + 1}
                  y={risks[index]}
                  r={6}
                  fill={markerColor}
                  stroke="transparent"
                  isFront
                />
              );
            })
            .filter(Boolean)}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}
